/**
 * AssignmentEditor (DNKADM-0055 U2) — a reusable multi-select that round-trips one of a
 * service's assignment sub-resources (categories / locations / eligible staff /
 * eligible resources, handoff §5.5).
 *
 * Options come from a fail-closed pick-list (`usePickList` → `<PickListLoader>`); the
 * current selection loads via `getIds(serviceId)` and is persisted as the WHOLE set via
 * `setIds(serviceId, ids)` on Save. The control tracks dirty state and disables Save
 * until the selection changes. An optional `minOne` enforces "≥1 selected" inline (used
 * for locations — a service must be offered somewhere). Every save refetches + toasts;
 * a validation `400`/`409` surfaces inline, anything else a toast (`classifyFormError`).
 *
 * **A failed load is an error, never an empty set (DNKADM-0395 U1).** The selection is
 * `null` until it loads; a failed `getIds` leaves it `null`, shows an inline error with
 * **Try again**, and keeps Save disabled. It must NEVER fall back to `[]`: `setIds` is a
 * WHOLE-SET replace, so a transient GET failure that rendered a staffed service as empty
 * would let one tick + Save delete everyone else (the 2026-09-17 staging wipe).
 *
 * **Clearing everything asks first (DNKADM-0395 U2).** With `confirmEmpty` supplied, saving
 * an EMPTY selection opens a confirm first; Cancel sends nothing, so the saved set stands.
 * The caller decides whether the dimension is clearable-without-asking (staff/resources set
 * to "Not used" pass no `confirmEmpty`).
 *
 * **Dismissing that confirm puts the box back (DNKADM-0397 U1).** Cancel used to close the
 * dialog and leave the emptied selection on screen, so an operator who chose safety saw
 * exactly what a successful wipe looks like — the editor asserting something false about the
 * saved data. Dismissal now restores `selected` from `initial`, the SAVED baseline already in
 * hand: no refetch (a GET here would reopen the very transient-failure window U1 closes) and
 * no reconstruction. Restoring the baseline also clears `dirty`, so Save goes back to
 * disabled on its own — the two states cannot disagree.
 *
 * **Embedded in a drawer (DNKADM-0493).** With `embedded`, the editor renders no Save of its
 * own: the service page's edit drawer owns the one Save and drives it through `handleRef`
 * (`save()` resolves `true` when there was nothing to send or the PUT succeeded, `false` when
 * it was refused, failed, or the clear-all confirm was dismissed). Every guard above is the
 * same: an unloaded selection never saves, `minOne` still refuses inline, and an empty save
 * still asks first. The request is the same whole-set PUT. In this mode an error of either
 * kind shows inline (the drawer stays open on it) and there is no per-editor success toast
 * (the drawer raises one for the whole save). `onStatusChange` reports `loaded`/`dirty` so the
 * drawer can hold its Save off while a selection has not loaded.
 */
import { useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { PickListLoader } from '@/components/PickListLoader';
import type { PickList } from '@/hooks/usePickList';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { ScreenError } from '@/components/ScreenError';

/** The minimal option every assignment pick-list yields. */
export interface IdNameOption {
  id: string;
  name: string;
  isActive: boolean;
}

/** The drawer's handle on an embedded editor (DNKADM-0493). */
export interface AssignmentEditorHandle {
  /** Save the selection if it changed. `true` = nothing to send or saved; `false` = not saved. */
  save: () => Promise<boolean>;
}

export interface AssignmentEditorProps {
  /** The owning service id. */
  serviceId: string;
  /** The control's accessible label (e.g. "Categories", "Locations"). */
  label: string;
  /** Lower-case noun woven into the pick-list loading/error copy. */
  pickListLabel: string;
  /** The fail-closed options pick-list. */
  list: PickList<IdNameOption>;
  /** Load the current selection (the assigned ids). */
  getIds: (serviceId: string) => Promise<string[]>;
  /**
   * Persist the WHOLE selection. `opts.clearAll` is passed ONLY on a save of an empty
   * selection — the operator emptied the box and saved it (through `confirmEmpty` when the
   * dimension asks first). A writer whose API needs an explicit clear (staff, DNKAPI-0637)
   * sends it; the others ignore it.
   */
  setIds: (serviceId: string, ids: string[], opts?: { clearAll?: boolean }) => Promise<void>;
  /** When true, require at least one selection (enforced inline). */
  minOne?: boolean;
  /**
   * When supplied, saving an EMPTY selection asks for confirmation first with this copy
   * (DNKADM-0395 U2). Omit it to let an empty set save straight through — which is the
   * right behaviour for a dimension the service does not use.
   */
  confirmEmpty?: { title: string; body: string } | undefined;
  /** Raise a non-blocking toast. */
  showToast: ShowToast;
  /** Called after a successful save (so the parent can recompute warnings). */
  onSaved?: (ids: string[]) => void;
  /** Render without a Save button; the owning drawer saves through `handleRef` (DNKADM-0493). */
  embedded?: boolean;
  /** The drawer's handle (embedded mode). */
  handleRef?: Ref<AssignmentEditorHandle>;
  /** Reports whether the selection has loaded and whether it changed (embedded mode). */
  onStatusChange?: (status: { loaded: boolean; dirty: boolean }) => void;
}

export function AssignmentEditor({
  serviceId,
  label,
  pickListLabel,
  list,
  getIds,
  setIds,
  minOne = false,
  confirmEmpty,
  showToast,
  onSaved,
  embedded = false,
  handleRef,
  onStatusChange,
}: AssignmentEditorProps) {
  const ids = useId();
  // `null` = not loaded (still loading, or the load FAILED). Never `[]` on failure — see
  // the file header: an assumed-empty selection is how a whole-set PUT wipes a real one.
  const [selected, setSelected] = useState<string[] | null>(null);
  const [initial, setInitial] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  // Bumped by "Try again" to re-run the load effect without remounting the control.
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Embedded mode: the drawer's pending `save()` while the clear-all confirm is open.
  const confirmResolve = useRef<((saved: boolean) => void) | null>(null);

  // Load the current selection once per service (and on each "Try again").
  useEffect(() => {
    let cancelled = false;
    setSelected(null);
    setInitial(null);
    setLoadError(null);
    void (async () => {
      try {
        const current = await getIds(serviceId);
        if (cancelled) return;
        setSelected(current);
        setInitial(current);
      } catch (err) {
        if (cancelled) return;
        // DNKADM-0395 U1: surface the failure and keep Save off. The selection and the
        // saved baseline both stay `null`, so nothing can be persisted over the real set.
        setLoadError(err ?? new Error('Assignment load failed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, getIds, reloadKey]);

  const dirty = useMemo(() => {
    if (initial === null || selected === null) return false;
    if (initial.length !== selected.length) return true;
    const init = new Set(initial);
    return selected.some((id) => !init.has(id));
  }, [initial, selected]);

  const loaded = selected !== null;
  const tooFew = minOne && loaded && selected.length === 0;

  useEffect(() => {
    onStatusChange?.({ loaded, dirty });
  }, [onStatusChange, loaded, dirty]);

  /** PUT the whole set. Only ever called with a loaded, validated selection. */
  async function persist(next: string[]): Promise<boolean> {
    setSaving(true);
    try {
      // DNKAPI-0637: an empty save is an explicit clear-all, said so on the wire. Persist only
      // ever runs on a LOADED selection the operator changed (Save is off until then), so an
      // empty `next` is always one they emptied themselves, never a failed load.
      if (next.length === 0) await setIds(serviceId, next, { clearAll: true });
      else await setIds(serviceId, next);
      setInitial(next);
      if (!embedded) showToast(`${label} saved`, 'success');
      onSaved?.(next);
      return true;
    } catch (err) {
      const view = classifyFormError(err, `Couldn’t save the ${pickListLabel}.`);
      // Embedded: the drawer stays open on the failure, so it is said right here.
      if (view.kind === 'inline' || embedded) setError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (selected === null) return;
    setError(null);
    if (tooFew) {
      setError(`Select at least one ${pickListLabel.replace(/s$/, '')}.`);
      return;
    }
    // DNKADM-0395 U2: clearing a dimension the service actually uses asks first. Unlike
    // `minOne` this is a confirmation, not a refusal — the empty set IS savable, once
    // the user says so.
    if (selected.length === 0 && confirmEmpty) {
      setConfirmOpen(true);
      return;
    }
    await persist(selected);
  }

  /**
   * DNKADM-0397 U1 — the confirm was dismissed (Cancel, Escape, backdrop), so nothing was
   * saved: put the editor back to the set that IS saved. `initial` is that baseline, held
   * since the load and refreshed by every successful `persist` — never refetched here.
   */
  function dismissConfirm() {
    setConfirmOpen(false);
    setSelected(initial);
    confirmResolve.current?.(false);
    confirmResolve.current = null;
  }

  async function confirmClear() {
    setConfirmOpen(false);
    const saved = await persist([]);
    confirmResolve.current?.(saved);
    confirmResolve.current = null;
  }

  // DNKADM-0493 — the drawer's Save. Same guards as the button path, in the same order.
  useImperativeHandle(handleRef, () => ({
    save: async () => {
      if (selected === null) return false; // never loaded: nothing may be written over the real set
      setError(null);
      if (!dirty) return true;
      if (tooFew) {
        setError(`Select at least one ${pickListLabel.replace(/s$/, '')}.`);
        return false;
      }
      if (selected.length === 0 && confirmEmpty) {
        return new Promise<boolean>((resolve) => {
          confirmResolve.current = resolve;
          setConfirmOpen(true);
        });
      }
      return persist(selected);
    },
  }));

  const labelId = `${ids}-label`;
  // The confirm dialog names itself from its own title (the sentence carrying WHICH service is
  // about to lose its pool) — MUI does not wire this pair up on its own.
  const confirmTitleId = `${ids}-confirm-title`;

  return (
    <PickListLoader list={list} label={pickListLabel}>
      {(options) => (
        <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
          <FormControl size="small" fullWidth disabled={saving || !loaded || options.length === 0}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
              labelId={labelId}
              multiple
              value={selected ?? []}
              onChange={(e) =>
                setSelected(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                )
              }
              input={<OutlinedInput label={label} />}
              slotProps={{ input: { 'aria-label': label } }}
              renderValue={(picked) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {picked.map((id) => {
                    const opt = options.find((o) => o.id === id);
                    const label = opt ? opt.name || opt.id : id;
                    const inactive = opt !== undefined && !opt.isActive;
                    return (
                      <Chip
                        key={id}
                        size="small"
                        label={inactive ? `${label} (Inactive)` : label}
                        variant={inactive ? 'outlined' : 'filled'}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={selected?.includes(opt.id) ?? false} size="small" />
                  <ListItemText
                    primary={opt.name === '' ? opt.id : opt.name}
                    slotProps={{
                      primary: { sx: { color: opt.isActive ? 'text.primary' : 'text.secondary' } },
                    }}
                  />
                  {!opt.isActive && (
                    <Chip size="small" label="Inactive" color="default" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {options.length === 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No {pickListLabel} to assign yet.
            </Alert>
          )}

          {/* DNKADM-0395 U1 — the load failed, so the control holds NO selection: say so
              and offer a retry rather than presenting an empty set as the truth. */}
          {loadError !== null && (
            <ScreenError
              error={loadError}
              what={`the assigned ${pickListLabel}`}
              onRetry={() => setReloadKey((k) => k + 1)}
              retryMessage={`Couldn’t load the assigned ${pickListLabel}. Saving is disabled until this loads.`}
              retryLabel="Try again"
              sx={{ py: 0.5 }}
            />
          )}

          {error && (
            <Alert severity="error" role="alert" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          {!embedded && (
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleSave()}
                disabled={saving || !loaded || !dirty}
              >
                {saving ? 'Saving…' : `Save ${label.toLowerCase()}`}
              </Button>
            </Box>
          )}

          {/* DNKADM-0395 U2 — the last-chance confirm before a clear-everything save.
              Cancel closes without a PUT, so the saved set is untouched; DNKADM-0397 U1 then
              repaints the box from that untouched set. Escape/backdrop take the same route as
              Cancel — they are dismissals too, and a dismissal that left the box empty would
              be the identical lie. */}
          {confirmEmpty && (
            <Dialog
              open={confirmOpen}
              onClose={dismissConfirm}
              aria-labelledby={confirmTitleId}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle id={confirmTitleId}>{confirmEmpty.title}</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontSize: 14 }}>{confirmEmpty.body}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={dismissConfirm} color="inherit">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void confirmClear()}
                >
                  Remove all
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Stack>
      )}
    </PickListLoader>
  );
}
